import { Skeleton } from "@/components/ui/feedback/skeleton";

import { cn } from "@/lib/utils";
import { abbreviateNumber, addCommasToNumbers } from "@/lib/utils/prettyName";
import type { ICellRendererParams } from "ag-grid-community";
import { type ReactNode, memo, useMemo } from "react";
import type { NarrativeClusterRowData } from "./NarrativeClustersTableStates";

type NumberFormat = "abbreviate" | "commas" | "none";

interface NarrativeClusterCellRendererProps {
  params: ICellRendererParams<NarrativeClusterRowData>;
  prefixRenderer?: ReactNode;
  suffixRenderer?: ReactNode;
  valueClassName?: string;
  wrapText?: boolean;
  numberFormat?: NumberFormat;
  showSentimentBadge?: boolean;
}

const NarrativeClusterCellRenderer = ({
  params,
  prefixRenderer,
  suffixRenderer,
  valueClassName,
  wrapText = false,
  numberFormat = "none",
  showSentimentBadge = false,
}: NarrativeClusterCellRendererProps) => {
  const { value, data } = params;

  const sentimentBadge = useMemo(() => {
    if (!showSentimentBadge || typeof value !== "string") return null;

    const sentimentCategory =
      value.toLowerCase() === "positive"
        ? "Positive"
        : value.toLowerCase() === "negative"
          ? "Negative"
          : value.toLowerCase() === "neutral"
            ? "Neutral"
            : null;

    if (!sentimentCategory) return null;

    return (
      <span
        className={cn(
          "px-2.5 py-1 rounded-md font-medium overflow-hidden text-ellipsis whitespace-nowrap",
          sentimentCategory === "Positive" && "text-green-700 bg-green-50",
          sentimentCategory === "Negative" && "text-red-700 bg-red-50",
          sentimentCategory === "Neutral" && "text-gray-600 bg-gray-50",
        )}
      >
        {sentimentCategory}
      </span>
    );
  }, [showSentimentBadge, value]);

  const formattedValue = useMemo(() => {
    if (showSentimentBadge && sentimentBadge) return "";
    if (value == null) return "-";
    if (typeof value !== "number") return String(value);

    switch (numberFormat) {
      case "abbreviate":
        return abbreviateNumber(value);
      case "commas":
        return addCommasToNumbers(value);
      default:
        return String(value);
    }
  }, [value, numberFormat, showSentimentBadge, sentimentBadge]);

  if (!data) {
    return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
  }

  const contentClassName = cn(
    valueClassName,
    wrapText
      ? "wrap-break-word leading-relaxed"
      : "overflow-hidden text-ellipsis whitespace-nowrap",
  );

  return (
    <div
      className={cn(
        "flex gap-1 min-w-0",
        wrapText ? "items-start" : "items-center",
      )}
    >
      {prefixRenderer && <div className="shrink-0">{prefixRenderer}</div>}
      {sentimentBadge ? (
        <div className="shrink-0">{sentimentBadge}</div>
      ) : (
        <span className={contentClassName}>{formattedValue}</span>
      )}
      {suffixRenderer && <div className="shrink-0">{suffixRenderer}</div>}
    </div>
  );
};

NarrativeClusterCellRenderer.displayName = "NarrativeClusterCellRenderer";

export default memo(NarrativeClusterCellRenderer);
